import type React from 'react'
import { useMemo, useState } from 'react'
import { Form, Input, Select, Button } from 'antd'
import { Horizontal, Vertical, Texto } from '@gravitate-js/excalibrr'
import type { SupplierConfig } from '@api/useSupplierReconciliation'
interface AddExternalValuePopoverProps {
  type: 'supplier' | 'terminal' | 'product'
  onSubmit: (values: {
    source_name: string
    source_id?: string
    counterparty_id?: string
    supplier_id?: string
    location_ids?: string[]
    product_ids?: string[]
  }) => Promise<void>
  counterpartyOptions?: Array<{
    supplier_id: string
    supplier_name: string
  }>
  supplierConfigs?: Array<SupplierConfig>
  terminalOptions?: Array<{
    id: string
    name: string
  }>
  productOptions?: Array<{
    id: string
    name: string
  }>
}

export function AddExternalValuePopover({
  type,
  onSubmit,
  supplierConfigs = [],
  terminalOptions = [],
  productOptions = [],
  counterpartyOptions = [],
}: AddExternalValuePopoverProps) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  // Transform supplier data for options
  const supplierOptions = useMemo(() => {
    if (!supplierConfigs) return []
    return supplierConfigs
      .map((supplier: SupplierConfig) => ({
        id: supplier.supplier_id,
        name: supplier.supplier_name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [supplierConfigs])
  async function handleSubmit() {
    try {
      setLoading(true)
      const values = await form.validateFields()
      await onSubmit(values)
      form.resetFields()
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  function getTitle() {
    switch (type) {
      case 'supplier':
        return 'Add External Supplier'
      case 'terminal':
        return 'Add External Terminal'
      case 'product':
        return 'Add External Product'
    }
  }

  return (
    <Vertical gap={16} style={{ width: 300 }}>
      <Texto weight='bold'>{getTitle()}</Texto>
      <Form form={form} layout='vertical'>
        {/* External Name - Required for all types */}
        <Form.Item name='source_name' label='Source Name' rules={[{ required: true, message: 'Name is required' }]}>
          <Input placeholder={`Enter external ${type} name`} data-testid='external-source-name-input' />
        </Form.Item>

        {/* Supplier Selection - Only for terminal and product */}
        {(type === 'terminal' || type === 'product') && (
          <Form.Item name='supplier_id' label='Supplier' rules={[{ required: true, message: 'Supplier is required' }]}>
            <Select
              placeholder='Select supplier'
              data-testid='external-supplier-select'
              options={supplierOptions.map((opt) => ({
                value: opt.id,
                label: opt.name,
              }))}
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        )}

        {/* Terminal Selection - Only for terminal type */}
        {type === 'terminal' && (
          <Form.Item name='location_ids' label='Map to Terminals'>
            <Select
              mode='multiple'
              placeholder='Select terminals (optional)'
              data-testid='external-terminal-select'
              options={terminalOptions.map((opt) => ({
                value: opt.id,
                label: opt.name || '',
              }))}
              showSearch
              allowClear
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        )}

        {/* Product Selection - Only for product type */}
        {type === 'product' && (
          <Form.Item name='product_ids' label='Map to Products'>
            <Select
              mode='multiple'
              placeholder='Select products (optional)'
              data-testid='external-product-select'
              options={productOptions.map((opt) => ({
                value: opt.id,
                label: opt.name || '',
              }))}
              showSearch
              allowClear
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        )}

        {/* Counterparty Selection - Only for supplier type */}
        {type === 'supplier' && (
          <Form.Item
            name='counterparty_id'
            label='Gravitate Counterparty'
            rules={[{ required: true, message: 'Counterparty is required' }]}
          >
            <Select
              placeholder='Select counterparty'
              options={counterpartyOptions
                .map((opt) => ({
                  value: opt.supplier_id,
                  label: opt.supplier_name,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        )}
      </Form>

      <Horizontal justifyContent='flex-end'>
        <Button
          type='primary'
          disabled={loading}
          onClick={handleSubmit}
          loading={loading}
          data-testid='add-external-submit-button'
        >
          Add {type}
        </Button>
      </Horizontal>
    </Vertical>
  )
}
